// useAdsCampaignConfig.js

import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BUTTON_SELECT_CONTENT,
  BUTTON_CONTINUE,
  CONFIRMATION_USAGE_TEXT,
  BUTTON_SELECT_POST,
  CREATE_ADS_CAMPAIGN_APP_ID_QUESTION,
  CREATE_ADS_CAMPAIGN_APP_PROMOTION_QUESTION,
  CREATE_ADS_CAMPAIGN_BUDGET_DAILY_QUESTION,
  CREATE_ADS_CAMPAIGN_BUDGET_DESCRIPTION_QUESTION,
  CREATE_ADS_CAMPAIGN_BUDGET_TOTAL_QUESTION,
  CREATE_ADS_CAMPAIGN_CATALOG_FOR_APP_INSTALL_QUESTION,
  CREATE_ADS_CAMPAIGN_CATALOG_QUESTION,
  CREATE_ADS_CAMPAIGN_DESTINATION_TYPE_QUESTION,
  CREATE_ADS_CAMPAIGN_DESTINATION_URL_QUESTION,
  CREATE_ADS_CAMPAIGN_IDENTITIES_ACCOUNTS_QUESTION,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_SERVICES_QUESTION,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_POSTS_QUESTION,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_PRODUCTS_QUESTION,
  CREATE_ADS_CAMPAIGN_OBJECTIVE_DESCRIPTION,
  CREATE_ADS_CAMPAIGN_OBJECTIVE_QUESTION,
  CREATE_ADS_CAMPAIGN_START_END_DATE_QUESTION,
  CREATE_ADS_CAMPAIGN_TRACKING_QUESTION,
  CREATE_ADS_CAMPAIGN_TYPE_BUDGET_QUESTION,
  FIELD_REQUIRED,
  FIELD_URL_VALID,
  FIELDS_REQUIRED,
  START_DATE_AND_END_DATE_VALIDATION,
  FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR,
  PRODUCT_FIELDS_REQUIRED,
  PRODUCT_URL_INVALID,
  SERVICE_FIELDS_REQUIRED,
  SERVICE_URL_INVALID,
  BUTTON_ADD_PRODUCT,
  BUTTON_ADD_SERVICE,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_PRODUCT,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_SERVICE,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_PRODUCT_SINGLE,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_SERVICE_SINGLE,
  COST_OF_CAMPAIGN,
  COST_OF_RESIZE,
} from "../../../i18n/keysTranslations";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import {
  ADS_CAMPAIGN_META_DESTINATION_TYPE_APP_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_CATALOG_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_VIDEO_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION,
  ADS_CAMPAIGN_OBJECTIVES_WITH_DESTINATION_TYPE_META,
  ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_META,
  ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
  ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION,
  FORMATS_MEDIA_META,
  META,
  MIN_DIMENSIONS_CONTENT,
  VIDEO_FORMATS,
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_TRACKING_ID,
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_APP_ID,
  ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION,
  BUSINESS_OFFERING_PHYSICAL_PRODUCT,
  ADS_CAMPAIGN_TEMPLATE_SINGULAR_OPTION,
  ADS_CAMPAIGN_CAROUSEL_FORMAT,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION,
  PLAN_TYPE_CREDITS,
} from "../../../utils/constants";
import IconButtonAddContent from "../../../components/Buttons/IconButtonAddContent";
import { getPathDatabaseByRole } from "../../../actions/auth";
import {
  formatMediaUrl,
  formatMediaUrlAdFromCreatify,
  getIsCarouselAdIsAvailable,
  getMinimumBudget,
} from "../../../services/adsCampaigns";
import { REGEX_URL } from "../../../utils/regex";
import {
  getCurrencyAdAccountPlatform,
  getIsBlockedActions,
} from "../../../actions/getters";
import {
  startGetBusinessOffering,
  startGetCurrencyTax,
} from "../../../actions/adsCampaigns";
import AddIcon from "@mui/icons-material/Add";
import { useCreditActionCosts } from "../../useCreditActionCosts";
import { usePlatformDefaultsInGenerateCampaign } from "../../platformDefaults/usePlatformDefaultsInGenerateCampaign";
import { useJumpToLastVisibleStep } from "../useJumpToLastVisibleStep";

const RESUME_APP_PROMOTION_ALIAS = { appPromotion: "osTarget" };
import { saveInitialPlatformDefaultsFromCampaign } from "../../platformDefaults/saveInitialPlatformDefaultsFromCampaign";
import { ConfirmAlert } from "../../../components/Alerts/Alerts";
import ConfirmUsage from "../../../components/Billing/ConfirmUsage";
import MetaAdaptPlacementsCheckbox from "../../../components/Platforms/Meta/GenerateCampaign/MetaAdaptPlacementsCheckbox";
import { startGetIsAppSumo } from "../../../actions/payments";
import {
  startGetByokConfiguration,
  startSaveByokConfiguration,
} from "../../../actions/byok";
import {
  openInsufficientCreditsPaywall,
  setShowModalSelectSubscription,
} from "../../../actions/ui";
import {
  resolveActionCostForPlanType,
  resolveCampaignUsageMultiplier,
} from "../../../utils/creditActionCosts";
import {
  estimateAdaptFormatsMultiplier,
  getMetaAdaptPlacementsEligibility,
} from "../../../utils/metaAdaptPlacementsEligibility";

export function useGenerateCampaignFormMeta({
  campaignID,
  generatedPlatforms = {},
  language,
  campaignContext,
  onCallbackSave = () => {},
  onExitToPlatformSelection,
  verifyBlockAlert,
  resumeValues = null,
}) {
  const STEPS = [
    "identities",
    "objective",
    "appPromotion",
    "destinationType",
    "appID",
    "catalogID",
    "trackingID",
    "destinationURL",
    "typeBudget",
    "dates",
    "budget",
    "mediaUrls",
  ];
  const [error, setError] = useState(null);
  const [currencyTax, setCurrencyTax] = useState(1);
  const [businessOffering, setBusinessOffering] = useState(null);
  const { control, watch, setValue, getValues } = useForm({
    defaultValues: {
      step: 0,
      objective: "",
      template: ADS_CAMPAIGN_TEMPLATE_SINGULAR_OPTION,
      integrationID: "",
      adAccountID: "",
      pageID: "",
      instagramID: "",
      destinationType: ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION,
      catalogID: "",
      trackingID: "",
      trackingCustomEventID: "",
      trackingOfflineID: "",
      destinationURL: "",
      typeBudget: "",
      budget: "",
      startDate: null,
      endDate: null,
      mediaUrls: [],
      multipleItems: [{ destinationUrl: "", mediaUrls: [] }],
      adaptCreativesToPlacements: true,
      appID: "",
      isIOS14Campaign: true,
    },
  });

  const { isLoadingDefaults, resumeSettled } =
    usePlatformDefaultsInGenerateCampaign({
      platform: META,
      setValue,
      getValues,
      resumeValues,
      resumeFieldAliases: RESUME_APP_PROMOTION_ALIAS,
    });

  const step = watch("step");
  const template = watch("template");
  const objective = watch("objective");
  const destinationType = watch("destinationType");
  const typeBudget = watch("typeBudget");
  const integrationID = watch("integrationID");
  const adAccountID = watch("adAccountID");
  const mediaUrls = watch("mediaUrls");
  const multipleItems = watch("multipleItems");
  const catalogID = watch("catalogID");

  const isCarouselAdsAvailable = getIsCarouselAdIsAvailable({
    platform: META,
    destinationType,
  });

  const isOnVideoEngagement =
    destinationType === ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_VIDEO_OPTION &&
    objective === ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION;

  const isOnPostEngagement =
    destinationType === ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION &&
    objective === ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION;

  const utils = {
    control,
    setValue,
    watch,
    getValues,
    campaignID,
    campaignContext,
    language,
    businessOffering,
  };

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const {
    ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
    ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
  } = useCreditActionCosts();
  const typeOfPlans = useSelectorApp((state) => state.payments?.typeOfPlans);
  const creditPricingVersion = useSelectorApp(
    (state) => state.payments?.creditPricingVersion
  );
  const creditsBalance = useSelectorApp((state) => {
    const usage = state.payments?.subscriptionPlanInfo?.usage || {};
    return usage[PLAN_TYPE_CREDITS] ?? usage.credits ?? 0;
  });
  const isPlanActive = useSelectorApp((state) => state.payments?.isPlanActive);
  const isTrial = useSelectorApp(
    (state) => state.payments?.subscriptionPlanInfo?.isTrial
  );

  const currency = dispatch(
    getCurrencyAdAccountPlatform({
      platform: META,
      integrationID: integrationID,
      adAccountID: adAccountID,
    })
  );

  const { userID } = dispatch(getPathDatabaseByRole());

  const actionsToValidAnswer = () => {
    setError(null);
  };

  const getCurrencyTax = async (currency) => {
    if (!currency) return;
    const { ok, data } = await dispatch(startGetCurrencyTax(currency));

    setCurrencyTax(ok ? data : 1);
  };

  const getInitialData = async () => {
    const response = await dispatch(startGetBusinessOffering());
    if (response.ok) {
      setBusinessOffering(response.data);
    } else {
      setBusinessOffering(null);
    }
  };

  const onFinished = async () => {
    const formValues = getValues();
    const isMultiple =
      formValues.template === ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION;

    const eligibility = getMetaAdaptPlacementsEligibility({
      template: formValues.template,
      mediaUrls: formValues.mediaUrls,
      multipleItems: formValues.multipleItems,
      destinationType: formValues.destinationType,
    });

    const adaptCreativesToPlacements = Boolean(
      eligibility.shouldShow && formValues.adaptCreativesToPlacements
    );

    let formatsMultiplier = 0;
    if (adaptCreativesToPlacements) {
      formatsMultiplier = await estimateAdaptFormatsMultiplier(
        eligibility.uniqueUrls
      );
    }

    const requestObject = {
      objective: formValues.objective,
      template: formValues.template,
      integrationID: formValues.integrationID,
      adAccountID: formValues.adAccountID,
      pageID: formValues.pageID,
      instagramID: formValues.instagramID,
      useFBPageAsIG: formValues.useFBPageAsIG || false,
      catalogID: formValues.catalogID,
      destinationType: formValues.destinationType,
      trackingID: formValues.trackingID,
      trackingCustomEventID: formValues.trackingCustomEventID,
      trackingOfflineID: formValues.trackingOfflineID,
      destinationURL: formValues.destinationURL,
      typeBudget: formValues.typeBudget,
      budget: formValues.budget,
      startDate: formValues.startDate,
      endDate: formValues.endDate,
      mediaUrls: isMultiple ? [] : formValues.mediaUrls,
      multipleItems: isMultiple ? formValues.multipleItems : [],
      appID: formValues.appID,
      isIOS14Campaign:
        objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION
          ? formValues.isIOS14Campaign
          : false,
      osTarget: formValues.appPromotion,
      platform: META,
      adaptCreativesToPlacements,
    };

    if (verifyBlockAlert) {
      const isCreditsPlan = typeOfPlans === PLAN_TYPE_CREDITS;

      if (isCreditsPlan) {
        const campaignMultiplier = resolveCampaignUsageMultiplier({
          creditPricingVersion,
          campaignExists: false,
          platform: META,
          generatedPlatforms,
        });
        const campaignCost = resolveActionCostForPlanType(
          ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
          PLAN_TYPE_CREDITS
        );
        const creativesCost = resolveActionCostForPlanType(
          ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
          PLAN_TYPE_CREDITS
        );
        const campaignSpend = (campaignCost.amount || 0) * campaignMultiplier;
        const resizeSpend = adaptCreativesToPlacements
          ? (creativesCost.amount || 0) * formatsMultiplier
          : 0;
        const spend = campaignSpend + resizeSpend;
        const meterKey = campaignCost.meterKey || PLAN_TYPE_CREDITS;
        const credits = Number(creditsBalance) || 0;
        const lineItems = [
          ...(campaignSpend > 0
            ? [{ labelKey: COST_OF_CAMPAIGN, amount: campaignSpend }]
            : []),
          ...(resizeSpend > 0
            ? [{ labelKey: COST_OF_RESIZE, amount: resizeSpend }]
            : []),
        ];
        let selectedPaymentMode = "credits";
        let skipSilentCreditChecks = false;

        if (!isPlanActive && !isTrial) {
          dispatch(setShowModalSelectSubscription(true));
          return;
        }

        if (spend > 0) {
          let isByokUsageConfirmBlocked = false;
          const isAppSumo = (await dispatch(startGetIsAppSumo())) === true;

          const confirmUsageProps = {
            typeOfPlans,
            actionType: ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION?.type,
            meterKey,
            spend,
            credits,
            lineItems,
            onConfirmBlockedChange: (blocked) => {
              isByokUsageConfirmBlocked = Boolean(blocked);
            },
          };

          if (isAppSumo) {
            const byokConfiguration =
              (await dispatch(startGetByokConfiguration())) || {};
            selectedPaymentMode =
              byokConfiguration?.defaultPaymentMode || "credits";
            confirmUsageProps.defaultPaymentMode = selectedPaymentMode;
            confirmUsageProps.onChangePaymentMode = (value) => {
              selectedPaymentMode = value;
              dispatch(
                startSaveByokConfiguration({
                  defaultPaymentMode: value,
                })
              );
            };
          }

          if (!isAppSumo && credits < spend) {
            dispatch(
              meterKey === PLAN_TYPE_CREDITS
                ? openInsufficientCreditsPaywall()
                : setShowModalSelectSubscription(true)
            );
            return;
          }

          const result = await ConfirmAlert({
            title: t(CONFIRMATION_USAGE_TEXT),
            html: <ConfirmUsage {...confirmUsageProps} />,
            confirmButtonText: t(BUTTON_CONTINUE),
            preConfirm: () => {
              if (isByokUsageConfirmBlocked) return false;
            },
          });

          if (!result.isConfirmed) {
            return;
          }

          if (
            isAppSumo &&
            selectedPaymentMode === "credits" &&
            credits < spend
          ) {
            dispatch(
              meterKey === PLAN_TYPE_CREDITS
                ? openInsufficientCreditsPaywall()
                : setShowModalSelectSubscription(true)
            );
            return;
          }

          if (isAppSumo && selectedPaymentMode === "byok") {
            skipSilentCreditChecks = true;
          }
        }

        if (!skipSilentCreditChecks) {
          const isCampaignBlocked = await dispatch(
            getIsBlockedActions({
              action: ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
              showUsageConfirmationAlert: false,
              campaignUsageContext: {
                platform: META,
                generatedPlatforms,
              },
            })
          );
          if (isCampaignBlocked) {
            return;
          }

          if (adaptCreativesToPlacements && formatsMultiplier > 0) {
            const isCreativesBlocked = await dispatch(
              getIsBlockedActions({
                action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
                multiplier: formatsMultiplier,
                showUsageConfirmationAlert: false,
              })
            );
            if (isCreativesBlocked) {
              return;
            }
          }
        }
      } else {
        const isBlocked = await dispatch(
          getIsBlockedActions({
            action: ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
            showUsageConfirmationAlert: true,
            campaignUsageContext: {
              platform: META,
              generatedPlatforms,
            },
          })
        );
        if (isBlocked) {
          return;
        }

        if (adaptCreativesToPlacements && formatsMultiplier > 0) {
          const isCreativesBlocked = await dispatch(
            getIsBlockedActions({
              action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
              multiplier: formatsMultiplier,
              showUsageConfirmationAlert: false,
            })
          );
          if (isCreativesBlocked) {
            return;
          }
        }
      }
    }

    void saveInitialPlatformDefaultsFromCampaign({
      dispatch,
      platform: META,
      formValues: getValues(),
    });

    onCallbackSave(requestObject);
  };

  useEffect(() => {
    if (currency) {
      getCurrencyTax(currency);
    }
    // eslint-disable-next-line
  }, [currency]);

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, [userID]);

  const { steps, fields } = useMemo(() => {
    const adaptEligibility = getMetaAdaptPlacementsEligibility({
      template,
      mediaUrls,
      multipleItems,
      destinationType,
    });

    const fieldsConfig = {
      objective: {
        isVisible: () => true,
        isFinishQuestion: false,
        utils,
        name: "objective",
        title: CREATE_ADS_CAMPAIGN_OBJECTIVE_QUESTION,
        subtitle: CREATE_ADS_CAMPAIGN_OBJECTIVE_DESCRIPTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const objective = getValues("objective");
          if (!objective || objective?.length === 0) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      appPromotion: {
        isVisible: () =>
          objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
        isFinishQuestion: false,
        utils,
        name: "appPromotion",
        title: CREATE_ADS_CAMPAIGN_APP_PROMOTION_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const appPromotion = getValues("appPromotion");
          if (!appPromotion) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      destinationType: {
        isVisible: () =>
          ADS_CAMPAIGN_OBJECTIVES_WITH_DESTINATION_TYPE_META.includes(
            objective
          ) && objective !== ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
        isFinishQuestion: false,
        utils,
        name: "destinationType",
        title: CREATE_ADS_CAMPAIGN_DESTINATION_TYPE_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const destinationType = getValues("destinationType");
          if (!destinationType) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      identities: {
        isVisible: () => true,
        isFinishQuestion: false,
        utils,
        name: "identities",
        title: CREATE_ADS_CAMPAIGN_IDENTITIES_ACCOUNTS_QUESTION,
        showActionsButtons: true,
        showPreviousButton: Boolean(onExitToPlatformSelection),
        onPreviousStep: () => {
          actionsToValidAnswer();
          onExitToPlatformSelection?.();
        },
        showNextButton: true,
        onNextStep: () => {
          const adAccountID = getValues("adAccountID");
          const pageID = getValues("pageID");

          if (!adAccountID || !pageID) {
            setError(t(FIELDS_REQUIRED));
            return false;
          }

          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      catalogID: {
        isVisible: () =>
          destinationType ===
            ADS_CAMPAIGN_META_DESTINATION_TYPE_CATALOG_OPTION ||
          objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
        isFinishQuestion: false,
        utils,
        name: "catalogID",
        title:
          objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION
            ? CREATE_ADS_CAMPAIGN_CATALOG_FOR_APP_INSTALL_QUESTION
            : CREATE_ADS_CAMPAIGN_CATALOG_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showSkipButton:
          objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
        onSkipStep: () => {
          setValue("step", step + 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const catalogID = getValues("catalogID");
          if (!catalogID) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      appID: {
        isVisible: () =>
          objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION ||
          (objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION &&
            destinationType ===
              ADS_CAMPAIGN_META_DESTINATION_TYPE_APP_OPTION) ||
          ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_APP_ID.includes(
            destinationType
          ),
        isFinishQuestion: false,
        utils,
        name: "appID",
        title: CREATE_ADS_CAMPAIGN_APP_ID_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const appID = getValues("appID");
          if (!appID) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      trackingID: {
        isVisible: () =>
          ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_META.includes(objective) &&
          ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_TRACKING_ID.includes(
            destinationType
          ),
        isFinishQuestion: false,
        utils,
        name: "trackingID",
        title: CREATE_ADS_CAMPAIGN_TRACKING_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const trackingID = getValues("trackingID");
          if (!trackingID) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      destinationURL: {
        isVisible: () =>
          destinationType ===
            ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION &&
          objective !== ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
        isFinishQuestion: false,
        utils,
        name: "destinationURL",
        title: CREATE_ADS_CAMPAIGN_DESTINATION_URL_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const destinationURL = getValues("destinationURL");
          if (!destinationURL) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          if (!REGEX_URL.test(encodeURI(destinationURL))) {
            setError(t(FIELD_URL_VALID));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      typeBudget: {
        isVisible: () => true,
        isFinishQuestion: false,
        utils,
        name: "typeBudget",
        title: CREATE_ADS_CAMPAIGN_TYPE_BUDGET_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const typeBudget = getValues("typeBudget");
          if (!typeBudget) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      budget: {
        isVisible: () => true,
        isFinishQuestion:
          destinationType ===
            ADS_CAMPAIGN_META_DESTINATION_TYPE_CATALOG_OPTION ||
          (objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION &&
            catalogID),
        onFinish: () => {
          const budget = getValues("budget");
          if (!budget) {
            setError(t(FIELD_REQUIRED));
            return false;
          }

          const minimumBudget = getMinimumBudget({
            platform: META,
            typeBudget,
            startDateTime: getValues("startDate"),
            endDateTime: getValues("endDate"),
            currencyTax,
            currency,
          });

          if (minimumBudget && budget < minimumBudget) {
            setError(
              t(FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR, {
                value: minimumBudget,
                currency: currency,
              })
            );
            return false;
          }

          actionsToValidAnswer();
          onFinished();
        },
        utils,
        name: "budget",
        title:
          typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION
            ? CREATE_ADS_CAMPAIGN_BUDGET_DAILY_QUESTION
            : CREATE_ADS_CAMPAIGN_BUDGET_TOTAL_QUESTION,
        subtitle: CREATE_ADS_CAMPAIGN_BUDGET_DESCRIPTION_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: !(
          destinationType ===
            ADS_CAMPAIGN_META_DESTINATION_TYPE_CATALOG_OPTION ||
          (objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION &&
            catalogID)
        ),
        onNextStep: () => {
          const budget = getValues("budget");
          if (!budget) {
            setError(t(FIELD_REQUIRED));
            return false;
          }

          const minimumBudget = getMinimumBudget({
            platform: META,
            typeBudget,
            startDateTime: getValues("startDate"),
            endDateTime: getValues("endDate"),
            currencyTax,
            currency,
          });

          if (minimumBudget && budget < minimumBudget) {
            setError(
              t(FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR, {
                value: minimumBudget,
                currency: currency,
              })
            );
            return false;
          }

          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      dates: {
        isVisible: () =>
          typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION,
        isFinishQuestion: false,
        utils,
        name: "dates",
        title: CREATE_ADS_CAMPAIGN_START_END_DATE_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION,
        onNextStep: () => {
          const startDate = getValues("startDate");
          const endDate = getValues("endDate");
          if (!startDate || !endDate) {
            setError(t(FIELDS_REQUIRED));
            return false;
          }

          if (startDate > endDate) {
            setError(t(START_DATE_AND_END_DATE_VALIDATION));
            return false;
          }

          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      mediaUrls: {
        isVisible: () =>
          !(
            destinationType ===
              ADS_CAMPAIGN_META_DESTINATION_TYPE_CATALOG_OPTION ||
            (objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION &&
              catalogID)
          ),
        isFinishQuestion: true,
        disabledFinishButton:
          template === ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION
            ? !multipleItems ||
              multipleItems.length === 0 ||
              multipleItems.every(
                (p) => !p.destinationUrl || p.mediaUrls.length === 0
              )
            : !mediaUrls || mediaUrls.length === 0,
        onFinish: () => {
          if (template === ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION) {
            const multipleItems = getValues("multipleItems");
            if (!multipleItems || multipleItems.length === 0) {
              setError(t(FIELD_REQUIRED));
              return false;
            }
            // Validate all items have destinationUrl and at least one mediaUrl
            const invalidItemIndex = multipleItems.findIndex(
              (p) => !p.destinationUrl || p.mediaUrls.length === 0
            );
            if (invalidItemIndex !== -1) {
              const businessOffering = utils.businessOffering;
              const isPhysicalProduct =
                businessOffering === BUSINESS_OFFERING_PHYSICAL_PRODUCT;
              setError(
                t(
                  isPhysicalProduct
                    ? PRODUCT_FIELDS_REQUIRED
                    : SERVICE_FIELDS_REQUIRED,
                  {
                    number: invalidItemIndex + 1,
                  }
                )
              );
              return false;
            }
            // Validate all URLs are valid
            const invalidUrlIndex = multipleItems.findIndex(
              (p) => !REGEX_URL.test(encodeURI(p.destinationUrl))
            );
            if (invalidUrlIndex !== -1) {
              const businessOffering = utils.businessOffering;
              const isPhysicalProduct =
                businessOffering === BUSINESS_OFFERING_PHYSICAL_PRODUCT;
              setError(
                t(
                  isPhysicalProduct ? PRODUCT_URL_INVALID : SERVICE_URL_INVALID,
                  {
                    number: invalidUrlIndex + 1,
                  }
                )
              );
              return false;
            }
          } else {
            const mediaUrls = getValues("mediaUrls");
            if (!mediaUrls || mediaUrls.length === 0) {
              setError(t(FIELD_REQUIRED));
              return false;
            }
          }

          actionsToValidAnswer();
          onFinished();
        },
        utils,
        name: "mediaUrls",
        title: isOnPostEngagement
          ? CREATE_ADS_CAMPAIGN_MEDIA_URL_POSTS_QUESTION
          : businessOffering === BUSINESS_OFFERING_PHYSICAL_PRODUCT
            ? CREATE_ADS_CAMPAIGN_MEDIA_URL_PRODUCTS_QUESTION
            : CREATE_ADS_CAMPAIGN_MEDIA_URL_SERVICES_QUESTION,
        subtitle:
          businessOffering === BUSINESS_OFFERING_PHYSICAL_PRODUCT
            ? template === ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION
              ? CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_PRODUCT_SINGLE
              : CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_PRODUCT
            : template === ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION
              ? CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_SERVICE_SINGLE
              : CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_SERVICE,
        icon: <AddIcon />,
        showTitleButton:
          (mediaUrls.length === 0 &&
            template === ADS_CAMPAIGN_TEMPLATE_SINGULAR_OPTION) ||
          template === ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION,
        buttonTitleText:
          businessOffering === BUSINESS_OFFERING_PHYSICAL_PRODUCT
            ? t(BUTTON_ADD_PRODUCT)
            : t(BUTTON_ADD_SERVICE),
        onClickButtonTitle: () => {
          if (template === ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION) {
            const multipleItems = getValues("multipleItems");
            setValue("multipleItems", [
              ...multipleItems,
              { destinationUrl: "", mediaUrls: [] },
            ]);
          } else {
            setValue("multipleItems", [{ destinationUrl: "", mediaUrls: [] }]);
            setValue("template", ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION);
            setValue("mediaUrls", []);
          }
        },
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        customComponentBelowActions: adaptEligibility.shouldShow ? (
          <MetaAdaptPlacementsCheckbox
            control={control}
            uniqueUrls={adaptEligibility.uniqueUrls}
            showCreditsCost={typeOfPlans === PLAN_TYPE_CREDITS}
          />
        ) : null,
        customComponentPreviousNext:
          template === ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION ? null : (
            <Controller
              name="mediaUrls"
              control={control}
              render={({ field }) => (
                <IconButtonAddContent
                  mode="button"
                  color="secondary"
                  size="large"
                  minDimensions={MIN_DIMENSIONS_CONTENT}
                  pathStorage={`adsCampaigns/${userID}/`}
                  pathRTDB={`adsContentsUploaded/${userID}/${campaignID}`}
                  modeUpload="sync"
                  initialValues={{ language, campaignContext, platform: META }}
                  onAddContent={(contents) => {
                    const contentsFormatted = contents.map((content) =>
                      formatMediaUrl(content)
                    );
                    field.onChange([...field.value, ...contentsFormatted]);
                  }}
                  onCallbackSaveAI={(videoData) => {
                    field.onChange([
                      ...field.value,
                      formatMediaUrlAdFromCreatify(videoData),
                    ]);
                  }}
                  onCallbackSaveCanva={(mediaUrl) => {
                    const canvaContent = formatMediaUrl({
                      url: mediaUrl,
                      type: "upload",
                    });
                    field.onChange([...field.value, canvaContent]);
                  }}
                  multiple={true}
                  acceptedFormats={
                    isOnVideoEngagement
                      ? VIDEO_FORMATS
                      : FORMATS_MEDIA_META.concat(
                          isCarouselAdsAvailable
                            ? [ADS_CAMPAIGN_CAROUSEL_FORMAT]
                            : []
                        )
                  }
                  textButton={
                    isOnPostEngagement
                      ? BUTTON_SELECT_POST
                      : BUTTON_SELECT_CONTENT
                  }
                  sx={{
                    alignItems: "flex-start",
                    width: { xs: "100%", md: "auto" },
                  }}
                  sxButton={{
                    height: 48,
                    width: "100%",
                  }}
                  destinationType={destinationType}
                  forcePostSelection={isOnPostEngagement}
                  propsPostsAdsPlatforms={{
                    [META]: {
                      integrationID: getValues("integrationID"),
                      adAccountID: getValues("adAccountID"),
                      pageID: getValues("pageID"),
                    },
                  }}
                />
              )}
            />
          ),
        showNextButton: false,
        onNextStep: () => {
          setValue("step", step + 1);
        },
      },
    };

    const stepsVisible = STEPS.filter((step) => fieldsConfig[step].isVisible());

    return {
      steps: stepsVisible,
      fields: fieldsConfig,
    };

    // eslint-disable-next-line
  }, [
    step,
    template,
    objective,
    destinationType,
    typeBudget,
    mediaUrls,
    multipleItems,
    catalogID,
    typeOfPlans,
    onExitToPlatformSelection,
  ]);

  const { hasJumped } = useJumpToLastVisibleStep({
    enabled: Boolean(resumeValues),
    ready: resumeSettled,
    steps,
    setValue,
  });
  const isApplyingResume = Boolean(resumeValues) && !hasJumped;
  const currentAnswer = isApplyingResume ? null : steps[step];
  const totalSteps = steps.length;
  const progress = Math.round(((step + 1) / totalSteps) * 100);

  return {
    steps,
    fields,
    utils,
    currentAnswer,
    error,
    progress,
    currency,
    currencyTax,
    isLoadingDefaults,
  };
}
